import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import useFartherEmployees from "../../Entity/hooks/useFartherEmployees";
import { AdvisorId } from "../../Types";
import useGetTaskActivity, { taskActivityKeyMaker } from "./useGetTaskActivity";
import useGetTaskComments, { taskCommentsKeyMaker } from "./useGetTaskComments";
const taskCommentsPagination = {
    pageSize: 999,
    page: 1,
    sort: "+createdTs",
};
const activityLogPagination = {
    page: "1",
    pageSize: "1000",
    orderBy: "timestamp desc",
};
const useGetTaskActivityLog = (taskId, auth) => {
    const queryClient = useQueryClient();
    const comments = useGetTaskComments(taskId, taskCommentsPagination, auth);
    const activities = useGetTaskActivity(taskId, activityLogPagination, auth);
    const humanModifierProfiles = useFartherEmployees("All", auth);
    const refetchTaskActivityLog = useCallback(async () => {
        await Promise.all([
            queryClient.refetchQueries(taskCommentsKeyMaker(taskId, taskCommentsPagination)),
            queryClient.refetchQueries(taskActivityKeyMaker(taskId, activityLogPagination)),
        ]);
    }, [queryClient, taskId]);
    if (comments.data?.values.length === 0 &&
        activities.data?.data.length === 0) {
        return { data: [], refetchTaskActivityLog };
    }
    const humanModifierIds = new Set((activities.data?.data ?? [])
        .map((a) => a.meta?.data.modifier.type === "HUMAN" ? a.meta.data.modifier.id : null)
        .filter((id) => id !== null));
    const humanModifierProfilesById = new Map(humanModifierProfiles.data?.advisors
        .filter((a) => humanModifierIds.has(a.advisorId))
        .map((a) => [a.advisorId, a]));
    const activitiesWithHumanModifierProfiles = activities.data?.data.map((a) => {
        if (a.meta?.data.modifier.type === "HUMAN") {
            return {
                ...a,
                meta: {
                    ...a.meta,
                    data: {
                        ...a.meta.data,
                        modifier: {
                            ...a.meta.data.modifier,
                            profile: humanModifierProfilesById.get(AdvisorId.parse(a.meta.data.modifier.id)),
                        },
                    },
                },
            };
        }
        return a;
    }) ?? [];
    const commentsWithHumanModifierProfiles = comments.data?.values.map((c) => {
        return {
            ...c,
            author: {
                ...c.author,
                roles: humanModifierProfilesById.get(AdvisorId.parse(c.author.id))?.roles ??
                    [],
            },
        };
    });
    const interlacedActivityAndComments = [
        ...(commentsWithHumanModifierProfiles ?? []),
        ...activitiesWithHumanModifierProfiles,
    ].sort((a, b) => {
        const aDate = "createdTs" in a ? a.createdTs : a.timestamp;
        const bDate = "createdTs" in b ? b.createdTs : b.timestamp;
        return new Date(aDate).getTime() - new Date(bDate).getTime();
    });
    return {
        refetchTaskActivityLog,
        data: interlacedActivityAndComments,
    };
};
export default useGetTaskActivityLog;
//# sourceMappingURL=useGetTaskActivityLog.js.map